import React, { useCallback, useState } from 'react';
import { PlayIcon } from '@heroicons/react/24/solid';
import {
  buildYouTubeEmbedUrl,
  buildYouTubeThumbnailUrl,
  type WalkthroughVideo,
} from '../../config/walkthroughVideos';

type YouTubeWalkthroughEmbedProps = {
  video: WalkthroughVideo;
  className?: string;
  /** When true, show iframe immediately (e.g. product page walkthrough). */
  autoLoad?: boolean;
};

export default function YouTubeWalkthroughEmbed({
  video,
  className = '',
  autoLoad = false,
}: YouTubeWalkthroughEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(autoLoad);
  const [thumbQuality, setThumbQuality] = useState<'maxresdefault' | 'hqdefault'>('maxresdefault');

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const thumbUrl = buildYouTubeThumbnailUrl(video.youtubeId, thumbQuality);
  const embedUrl = buildYouTubeEmbedUrl(video.youtubeId);

  return (
    <div
      className={`relative aspect-video w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-900 shadow-[0_22px_60px_-26px_rgba(2,6,23,0.35)] ring-1 ring-slate-200/60 ${className}`}
    >
      {isPlaying ? (
        <iframe
          src={embedUrl}
          title={video.title}
          className="absolute inset-0 h-full w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={handlePlay}
          className="group absolute inset-0 flex h-full w-full items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
          aria-label={`Play video: ${video.title}`}
        >
          <img
            src={thumbUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            onError={() => {
              if (thumbQuality === 'maxresdefault') {
                setThumbQuality('hqdefault');
              }
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-slate-950/10 transition-opacity group-hover:from-slate-950/75" />
          <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-sky-600 shadow-xl transition-transform duration-300 group-hover:scale-110 sm:h-20 sm:w-20">
            <PlayIcon className="ml-1 h-8 w-8 sm:h-9 sm:w-9" aria-hidden />
          </span>
        </button>
      )}
    </div>
  );
}
